import type {
  GbuFassungInhalt,
  GbuHinweis,
  GbuInhaltGefaehrdung,
  GbuRolle,
  GbuVoll,
  Kataster,
  MassnahmeArt,
  MassnahmeListenZeile,
  RisikoStufe,
  SifaGbu,
  SifaGbuFassung,
  SifaGbuGefaehrdung,
  SifaGbuMassnahme,
  SifaGbuVorsorge,
  VorsorgeArt,
} from './gbuTypes';
import { isoDatum, naechsteFaelligkeit } from './fristenLogic';
import { GEFAEHRDUNGSFAKTOREN_FASSUNG, faktorLabel } from './kataloge/gefaehrdungsfaktoren';
import { PSA_KATALOG_FASSUNG, psaLabel } from './kataloge/psaKatalog';
import { ARBMEDVV_FASSUNG, anlassEintrag, anlassLabel } from './kataloge/arbmedvvAnlaesse';

/**
 * Gefaehrdungsbeurteilung (GBU) — REINE Fachlogik: Risiko, feste Regeln,
 * Vorsorgevorschlag, Pruefdatum, eingefrorener Fassungsinhalt und die
 * Massnahmenliste. Kein Supabase, kein React; `heute` kommt vom Aufrufer
 * (Ausnahme: `pruefeGbu` faellt ohne Angabe auf den lokalen Kalendertag
 * zurueck, siehe dort).
 *
 * Bauplan: docs/sifa-gbu-plan.md §6.1 (feste Regeln), §8 (Freigabe),
 * §14.4 B (Schnittstellen). Die Bedienregeln der Oberflaeche stehen
 * getrennt in components/sifa/gbu/gbuModulLogic.ts.
 *
 * Datumsangaben sind ISO-Zeichenketten (yyyy-mm-dd) und werden als solche
 * verglichen bzw. ueber fristenLogic gerechnet — nie `new Date('yyyy-mm-dd')`
 * (Zeitzonenfehler, Lehre zertifikatPdf.ts).
 */

// ─── Risiko ─────────────────────────────────────────────────────────────────

/** W bzw. S auf die Skala 1–3: gerundet, ausserhalb geklemmt, Unsinn → 1. */
const stufe13 = (n: number): number => {
  const v = Math.round(Number(n));
  if (!Number.isFinite(v)) return 1;
  return Math.min(3, Math.max(1, v));
};

/**
 * R = W × S auf der 3×3-Matrix (Plan §2: „R wird gerechnet, nie getippt").
 * Die Datenbank prueft W und S auf 1–3; hier wird trotzdem geklemmt, damit
 * ein halb ausgefuellter Entwurf im Editor nie R = 0 oder R = 12 zeigt.
 */
export const risiko = (w: number, s: number): number => stufe13(w) * stufe13(s);

/** 1–2 gering (gruen), 3–4 mittel (gelb), 6–9 hoch (rot). 5, 7, 8 kommen in 3×3 nicht vor. */
export const risikoStufe = (r: number): RisikoStufe => {
  if (r >= 6) return 'hoch';
  if (r >= 3) return 'mittel';
  return 'gering';
};

/** Ab diesem R gilt ein Risiko als hoch (Warnung „R nachher ≥ 6", O4). */
export const RISIKO_HOCH_AB = 6;

// ─── Aktive Zeilen ──────────────────────────────────────────────────────────

/**
 * Ist die Zeile im aktuellen Stand aktiv? Nach der ersten Freigabe werden
 * Gefaehrdungen, Massnahmen und Vorsorge nicht geloescht, sondern ab einer
 * Fassung als entfallen markiert (`entfaelltAbFassung`) — die gueltige
 * Fassung und ihre Umsetzungs- und Wirksamkeitsnachweise bleiben so
 * erhalten. Fehlt das Feld (Altzeile), gilt die Zeile als aktiv.
 */
export const istAktiv = (zeile: { entfaelltAbFassung?: number | null } | null | undefined): boolean =>
  !!zeile && (zeile.entfaelltAbFassung === null || zeile.entfaelltAbFassung === undefined);

// ─── Reihenfolge ────────────────────────────────────────────────────────────

const nachReihenfolge = <T extends { sortOrder: number; createdAt: string; id: string }>(a: T, b: T): number =>
  (a.sortOrder ?? 0) - (b.sortOrder ?? 0)
  || (a.createdAt || '').localeCompare(b.createdAt || '')
  || a.id.localeCompare(b.id);

/**
 * Gefaehrdungen in der Reihenfolge der GBU (sortOrder, bei Gleichstand
 * Anlagezeit, dann id — damit die Nummerierung 1..n stabil ist). Reine
 * Sortierung OHNE Filter; fuer die Nummer im PDF und in den Hinweisen zaehlt
 * `aktiveGefaehrdungen`.
 */
export const ordneGefaehrdungen = (gefaehrdungen: ReadonlyArray<SifaGbuGefaehrdung>): SifaGbuGefaehrdung[] =>
  [...gefaehrdungen].sort(nachReihenfolge);

/**
 * Die aktiven Gefaehrdungen einer GBU in ihrer Reihenfolge — die Position in
 * dieser Liste IST die Nummer 1..n im PDF, in der Fassung und in den
 * Hinweisen. Entfallene Gefaehrdungen fehlen samt ihrer Nummer.
 */
export const aktiveGefaehrdungen = (voll: Pick<GbuVoll, 'gefaehrdungen'> | null | undefined): SifaGbuGefaehrdung[] =>
  ordneGefaehrdungen((voll?.gefaehrdungen || []).filter(istAktiv));

/** Aktive Massnahmen einer Gefaehrdung nach sortOrder. */
const massnahmenVon = (massnahmen: ReadonlyArray<SifaGbuMassnahme>, gefaehrdungId: string): SifaGbuMassnahme[] =>
  massnahmen.filter((m) => m.gefaehrdungId === gefaehrdungId && istAktiv(m)).sort(nachReihenfolge);

/** Aktive Vorsorgeanlaesse einer Gefaehrdung nach Anlage. */
const vorsorgeVon = (vorsorge: ReadonlyArray<SifaGbuVorsorge>, gefaehrdungId: string): SifaGbuVorsorge[] =>
  vorsorge
    .filter((v) => v.gefaehrdungId === gefaehrdungId && istAktiv(v))
    .sort((a, b) => (a.createdAt || '').localeCompare(b.createdAt || '') || a.id.localeCompare(b.id));

// ─── Text ───────────────────────────────────────────────────────────────────

/** Kleinbuchstaben, Umlaute ausgeschrieben, alles andere als Leerzeichen — fuer Textabgleiche. */
const normalisiere = (text: string | null | undefined): string =>
  (text || '')
    .toLowerCase()
    .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

const kurz = (text: string, max = 60): string => {
  const t = (text || '').trim().replace(/\s+/g, ' ');
  return t.length > max ? `${t.slice(0, max - 1)}…` : t;
};

/** „Gefährdung 3 („Absturz …")" — so heisst eine Zeile in jedem Hinweis. */
const benenne = (g: SifaGbuGefaehrdung, nr: number): string => {
  const text = kurz(g.gefaehrdung || '');
  return text ? `Gefährdung ${nr} („${text}")` : `Gefährdung ${nr}`;
};

/** ISO-Datum → tt.mm.jjjj per Zeichenkette; sonst wortgetreu. */
const deDatum = (iso: string): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec((iso || '').trim());
  return m ? `${m[3]}.${m[2]}.${m[1]}` : iso;
};

const tag = (iso: string | null | undefined): string => (iso || '').trim().slice(0, 10);

// ─── Feste Regeln ───────────────────────────────────────────────────────────

// Ohne Wortgrenze: „Baulärm", „Maschinenlärm" — „Alarm" trifft nicht („larm" ohne e).
const LAERM = /laerm/;
const VIBRATION = /vibration|schwingung/;

/**
 * Braucht die Gefaehrdung voraussichtlich einen Vorsorgeanlass? Gefahrstoff
 * am Faktor, Laerm oder Vibration am Text (Gefaehrdung und Folgen) — beim
 * Text unabhaengig vom Faktor, weil Laerm auch unter „Arbeitsumgebung" und
 * eine Ruettelplatte auch unter „mechanisch" eingetragen wird.
 */
const vorsorgeThema = (g: SifaGbuGefaehrdung): string | null => {
  if (g.faktor === 'gefahrstoffe') return 'Gefahrstoff';
  const text = normalisiere(`${g.gefaehrdung || ''} ${g.folgen || ''}`);
  if (LAERM.test(text)) return 'Lärm';
  if (VIBRATION.test(text)) return 'Vibration';
  return null;
};

/** Stoffnamen kuerzer als das treffen im Freitext zu viel („Öl" in „Kölner …"). */
const MIN_STOFFNAME = 4;
/** Ab dieser Laenge zaehlt auch ein einzelnes Wort des Namens („Zement" aus „Zement/Frischbeton"). */
const MIN_STOFFWORT = 5;

/** Steht der Stoff im (normalisierten) Text — als ganzer Name oder mit einem markanten Wort? */
const stoffImText = (bezeichnung: string, text: string): boolean => {
  if (!text) return false;
  const name = normalisiere(bezeichnung);
  if (name.replace(/ /g, '').length >= MIN_STOFFNAME && text.includes(name)) return true;
  return name.split(' ').some((w) => w.length >= MIN_STOFFWORT && text.includes(w));
};

const STUFEN_RANG: Record<GbuHinweis['stufe'], number> = { sperre: 0, warnung: 1, hinweis: 2 };

/**
 * Die festen Regeln einer GBU (Plan §6.1, §14.4 B) — laufen immer, ohne KI.
 * Geprueft wird nur der AKTIVE Stand: entfallene Gefaehrdungen, Massnahmen
 * und Vorsorgeanlaesse (`entfaelltAbFassung`) zaehlen nicht mit.
 *
 * SPERREN (verhindern das Einfrieren, `kannEinfrieren`):
 *   titel_fehlt, bereich_fehlt (leer oder nicht im Kataster),
 *   keine_gefaehrdung, gefaehrdung_ohne_text.
 * WARNUNGEN (die Freigabe bleibt moeglich, O4):
 *   risiko_nachher_hoch    R nachher ≥ 6
 *   ohne_massnahme         Gefaehrdung ohne Massnahme
 *   nur_psa                R vorher ≥ 6, aber nur P-Massnahmen (STOP-Rangfolge)
 *   vorsorge_fehlt         Gefahrstoff, Laerm oder Vibration ohne Vorsorgeanlass
 *   stoff_ohne_gefaehrdung Stoff des Katasters steht in Bezeichnung oder
 *                          Beschreibung des Arbeitsbereichs, keine Gefaehrdung
 *                          verweist auf ihn
 *   stoff_ohne_stoffgefaehrdung  Stoff ist mit der GBU verknuepft, aber keine
 *                          Gefahrstoff- (bzw. Biostoff-)Gefaehrdung verweist
 *                          auf ihn — z. B. Zement nur bei „Heben und Tragen"
 *   pruefdatum_ueberschritten  `pruefenBis` liegt vor `heute`
 *
 * „Im Arbeitsbereich" heisst fuer einen Stoff: das Kataster kennt keine
 * feste Zuordnung Stoff ↔ Bereich (Plan §4.2: die Bereiche ergeben sich aus
 * den GBU-Verweisen). Erkennbar ist er deshalb nur ueber den Namen im Text
 * des Bereichs oder ueber einen Verweis aus der GBU selbst.
 *
 * `heute` ohne Angabe = lokaler Kalendertag des Geraets; Tests und Aufrufer
 * mit fester Uhr geben ihn mit.
 *
 * Reihenfolge: Sperren vor Warnungen, innerhalb einer Stufe GBU-weit vor den
 * Gefaehrdungen (in deren Nummernfolge), dann die Stoffe.
 */
export const pruefeGbu = (voll: GbuVoll, kataster: Kataster, heute: string = isoDatum(new Date())): GbuHinweis[] => {
  const hinweise: GbuHinweis[] = [];
  const add = (h: GbuHinweis) => hinweise.push(h);
  const { gbu } = voll;

  if (!(gbu.titel || '').trim()) {
    add({ code: 'titel_fehlt', stufe: 'sperre', text: 'Die Gefährdungsbeurteilung braucht einen Titel.' });
  }
  const bereich = (kataster.bereiche || []).find((b) => b.id === gbu.bereichId) ?? null;
  if (!gbu.bereichId) {
    add({ code: 'bereich_fehlt', stufe: 'sperre', text: 'Es ist kein Arbeitsbereich gewählt.' });
  } else if (!bereich) {
    add({ code: 'bereich_fehlt', stufe: 'sperre', text: 'Der gewählte Arbeitsbereich steht nicht mehr im Betriebskataster.' });
  }

  const pruefenBis = tag(gbu.pruefenBis);
  if (pruefenBis && gbu.status !== 'archiviert' && pruefenBis < tag(heute)) {
    add({
      code: 'pruefdatum_ueberschritten',
      stufe: 'warnung',
      text: `Die Überprüfung der Gefährdungsbeurteilung war bis ${deDatum(pruefenBis)} fällig.`,
    });
  }

  const gefaehrdungen = aktiveGefaehrdungen(voll);
  const massnahmen = voll.massnahmen || [];
  const vorsorge = voll.vorsorge || [];
  if (gefaehrdungen.length === 0) {
    add({ code: 'keine_gefaehrdung', stufe: 'sperre', text: 'Die Gefährdungsbeurteilung enthält noch keine Gefährdung.' });
  }

  gefaehrdungen.forEach((g, i) => {
    const nr = i + 1;
    const name = benenne(g, nr);
    if (!(g.gefaehrdung || '').trim()) {
      add({ code: 'gefaehrdung_ohne_text', stufe: 'sperre', text: `Gefährdung ${nr}: die Beschreibung fehlt.`, gefaehrdungId: g.id });
    }

    const rNach = risiko(g.wNach, g.sNach);
    if (rNach >= RISIKO_HOCH_AB) {
      add({
        code: 'risiko_nachher_hoch',
        stufe: 'warnung',
        text: `${name}: Das Risiko nach den Maßnahmen ist hoch (R = ${rNach}). Weitere Maßnahmen prüfen; die Freigabe bleibt möglich.`,
        gefaehrdungId: g.id,
      });
    }

    const eigene = massnahmenVon(massnahmen, g.id);
    if (eigene.length === 0) {
      add({ code: 'ohne_massnahme', stufe: 'warnung', text: `${name}: Es ist keine Maßnahme festgelegt.`, gefaehrdungId: g.id });
    } else {
      const rVor = risiko(g.wVor, g.sVor);
      if (rVor >= RISIKO_HOCH_AB && eigene.every((m) => m.art === 'P')) {
        add({
          code: 'nur_psa',
          stufe: 'warnung',
          text: `${name}: Das hohe Ausgangsrisiko (R = ${rVor}) wird nur mit persönlicher Schutzausrüstung abgedeckt. Nach der STOP-Rangfolge zuerst Substitution, technische und organisatorische Maßnahmen prüfen.`,
          gefaehrdungId: g.id,
        });
      }
    }

    const thema = vorsorgeThema(g);
    if (thema && vorsorgeVon(vorsorge, g.id).length === 0) {
      add({
        code: 'vorsorge_fehlt',
        stufe: 'warnung',
        text: `${name}: ${thema} ohne Vorsorgeanlass. Arbeitsmedizinische Vorsorge nach ArbMedVV prüfen.`,
        gefaehrdungId: g.id,
      });
    }
  });

  // ── Stoffe ────────────────────────────────────────────────────────────────
  const verwiesen = new Set<string>();
  const mitStoffgefaehrdung = new Set<string>();
  for (const g of gefaehrdungen) {
    for (const id of g.stoffIds || []) {
      verwiesen.add(id);
      if (g.faktor === 'gefahrstoffe' || g.faktor === 'biologisch') mitStoffgefaehrdung.add(`${g.faktor}:${id}`);
    }
  }
  const bereichsText = bereich ? normalisiere(`${bereich.bezeichnung} ${bereich.beschreibung || ''}`) : '';
  for (const stoff of kataster.stoffe || []) {
    if (stoff.aktiv !== false && !verwiesen.has(stoff.id) && stoffImText(stoff.bezeichnung, bereichsText)) {
      add({
        code: 'stoff_ohne_gefaehrdung',
        stufe: 'warnung',
        text: `${stoff.art === 'biostoff' ? 'Biostoff' : 'Gefahrstoff'} „${stoff.bezeichnung}" kommt im Arbeitsbereich vor, aber keine Gefährdung verweist darauf.`,
      });
    }
  }
  for (const stoff of kataster.stoffe || []) {
    if (!verwiesen.has(stoff.id)) continue;
    const faktor = stoff.art === 'biostoff' ? 'biologisch' : 'gefahrstoffe';
    if (!mitStoffgefaehrdung.has(`${faktor}:${stoff.id}`)) {
      add({
        code: 'stoff_ohne_stoffgefaehrdung',
        stufe: 'warnung',
        text: stoff.art === 'biostoff'
          ? `Biostoff „${stoff.bezeichnung}" ist verknüpft, aber keine Gefährdung durch biologische Arbeitsstoffe betrachtet ihn.`
          : `Gefahrstoff „${stoff.bezeichnung}" ist verknüpft, aber keine Gefahrstoff-Gefährdung (Haut, Atemwege) betrachtet ihn.`,
      });
    }
  }

  // Stabil nach Stufe — Array.prototype.sort ist seit ES2019 stabil.
  return hinweise.sort((a, b) => STUFEN_RANG[a.stufe] - STUFEN_RANG[b.stufe]);
};

/** Eingefroren werden darf, solange keine Sperre vorliegt (Warnungen sperren nie, O4). */
export const kannEinfrieren = (hinweise: ReadonlyArray<GbuHinweis>): boolean =>
  !hinweise.some((h) => h.stufe === 'sperre');

// ─── Vorsorge ───────────────────────────────────────────────────────────────

/** Einheit in eine Vergleichsform: 'dB(A)', 'dB(C)', 'm/s²', 'h/Tag' oder der bereinigte Text. */
const einheitSchluessel = (einheit: string): string => {
  const e = einheit.toLowerCase().replace(/\s+/g, '');
  if (e.includes('db')) return e.includes('c') ? 'db(c)' : 'db(a)';
  if (e.includes('m/s')) return 'm/s2';
  if (/^(h|std|stunde)/.test(e)) return 'h/tag';
  return e;
};

/**
 * Vorgeschlagene Vorsorgeart aus der Katalogregel und dem Messwert
 * (`sifa_gbu_vorsorge.art_vorschlag`).
 *
 *   messwert ≥ pflichtAb     → 'pflicht'   (Verordnung: „erreicht oder ueberschritten")
 *   messwert > angebotUeber  → 'angebot'   (Verordnung: „ueberschritten")
 *   sonst                    → 'wunsch'    (§ 5a ArbMedVV — unterhalb der Schwellen
 *                                           bleibt nur die Wunschvorsorge)
 *
 * null, wenn es nichts zu rechnen gibt: unbekannter Code, Anlass ohne Regel,
 * kein oder unbrauchbarer Messwert — oder eine `einheit`, die nicht zur Regel
 * passt (137 dB(C) ist kein Tages-Laermpegel von 137 dB(A)). Ohne Einheit
 * gilt die der Regel. Die bestaetigte Art (`art`) setzt immer der Mensch.
 */
export const vorsorgeArtVorschlag = (
  anlassCode: string,
  messwert: number | null | undefined,
  einheit?: string | null,
): VorsorgeArt | null => {
  const regel = anlassEintrag(anlassCode)?.regel ?? null;
  if (!regel) return null;
  if (messwert === null || messwert === undefined || !Number.isFinite(messwert) || messwert < 0) return null;
  if (einheit && einheit.trim() && einheitSchluessel(einheit) !== einheitSchluessel(regel.einheit)) return null;
  if (regel.pflichtAb !== null && messwert >= regel.pflichtAb) return 'pflicht';
  if (regel.angebotUeber !== null && messwert > regel.angebotUeber) return 'angebot';
  return 'wunsch';
};

// ─── Pruefdatum ─────────────────────────────────────────────────────────────

/**
 * Naechste Ueberpruefung: `isoDatum` + `monate`, Tag auf das Monatsende
 * geklemmt (31.01. + 1 Monat = 28./29.02.). Dieselbe Rechnung wie die
 * Fristen (fristenLogic.naechsteFaelligkeit), damit die GBU-Frist und
 * `pruefen_bis` nie um einen Tag auseinanderlaufen. Wirft bei unlesbarem
 * Datum oder Monaten ausserhalb 1–120 — ein still falsches Pruefdatum
 * waere schlimmer.
 */
export const naechstePruefung = (isoDatumText: string, monate = 12): string =>
  naechsteFaelligkeit(isoDatumText, monate);

// ─── Fassungsinhalt ─────────────────────────────────────────────────────────

export interface GbuFassungsMeta {
  kundeName: string;
  kundeAnschrift?: string | null;
  bueroName: string;
  /** ISO-Zeitstempel des Einfrierens. */
  erstelltAm: string;
  /** ISO-Datum der naechsten Ueberpruefung (naechstePruefung). */
  pruefenBis: string;
}

const textListe = (werte: ReadonlyArray<string> | null | undefined): string[] =>
  (werte || []).map((w) => (w || '').trim()).filter((w) => w !== '');

const rolle = (r: GbuRolle | null | undefined): GbuRolle => ({ name: r?.name ?? '', funktion: r?.funktion ?? '' });

/**
 * Der eingefrorene, selbsttragende Inhalt einer Fassung (`sifa_gbu_fassungen.inhalt`).
 *
 * Namen aus Kataster und Katalogen werden AUFGELOEST (Arbeitsbereich,
 * Arbeitsmittel, Stoffe, Faktor-, PSA- und Anlass-Labels), damit PDF, Excel,
 * Kundenportal und Freigabeseite ohne weitere Abfrage auskommen und ein
 * spaeter umbenannter Katastereintrag die unterschriebene Fassung nicht
 * veraendert. Nur AKTIVE Zeilen (`istAktiv`): eine entfallene Gefaehrdung
 * fehlt samt ihren Massnahmen und Vorsorgeanlaessen, ebenso einzeln
 * entfallene Massnahmen und Vorsorge. Gefaehrdungen nummeriert 1..n nach
 * `sortOrder`, Massnahmen nach `sortOrder`; R wird aus W und S gerechnet.
 * Verweise auf geloeschte Katastereintraege entfallen. Die Fassungsnummern
 * der Kataloge werden mitgeschrieben.
 *
 * Alles wird KOPIERT — eine spaetere Aenderung am Editor-Stand darf den
 * gebauten Inhalt nicht mitveraendern.
 */
export const baueFassungsInhalt = (voll: GbuVoll, kataster: Kataster, meta: GbuFassungsMeta): GbuFassungInhalt => {
  const { gbu } = voll;
  const bereich = (kataster.bereiche || []).find((b) => b.id === gbu.bereichId) ?? null;
  const arbeitsmittel = new Map((kataster.arbeitsmittel || []).map((a) => [a.id, a.bezeichnung]));
  const stoffe = new Map((kataster.stoffe || []).map((s) => [s.id, s.bezeichnung]));
  const aufloesen = (ids: ReadonlyArray<string> | null | undefined, namen: Map<string, string>): string[] =>
    (ids || []).map((id) => namen.get(id)).filter((n): n is string => typeof n === 'string');

  const massnahmen = voll.massnahmen || [];
  const vorsorge = voll.vorsorge || [];
  const gefaehrdungen: GbuInhaltGefaehrdung[] = aktiveGefaehrdungen(voll).map((g, i) => ({
    id: g.id,
    nr: i + 1,
    faktor: g.faktor,
    faktorLabel: faktorLabel(g.faktor),
    gefaehrdung: (g.gefaehrdung || '').trim(),
    folgen: g.folgen ?? null,
    wVor: stufe13(g.wVor), sVor: stufe13(g.sVor), rVor: risiko(g.wVor, g.sVor),
    wNach: stufe13(g.wNach), sNach: stufe13(g.sNach), rNach: risiko(g.wNach, g.sNach),
    arbeitsmittel: aufloesen(g.arbeitsmittelIds, arbeitsmittel),
    stoffe: aufloesen(g.stoffIds, stoffe),
    vorschriften: textListe(g.vorschriften),
    massnahmen: massnahmenVon(massnahmen, g.id).map((m) => ({
      id: m.id,
      art: m.art,
      text: (m.text || '').trim(),
      psaCode: m.psaCode ?? null,
      psaLabel: m.psaCode ? psaLabel(m.psaCode) : null,
      verantwortlich: m.verantwortlich ?? null,
      termin: m.termin ?? null,
    })),
    vorsorge: vorsorgeVon(vorsorge, g.id).map((v) => ({
      anlassCode: v.anlassCode,
      anlassLabel: anlassLabel(v.anlassCode),
      art: v.art,
      messwert: v.messwert ?? null,
      einheit: v.einheit ?? null,
    })),
  }));

  return {
    schema: 1,
    gbuId: gbu.id,
    nummer: gbu.nummer,
    fassung: gbu.fassung,
    titel: (gbu.titel || '').trim(),
    anlass: gbu.anlass,
    kunde: { name: meta.kundeName, anschrift: meta.kundeAnschrift ?? null },
    buero: { name: meta.bueroName },
    bereich: {
      bezeichnung: bereich?.bezeichnung ?? '',
      bereich: bereich?.bereich ?? null,
      beschreibung: bereich?.beschreibung ?? null,
      ort: bereich?.ort ?? null,
    },
    kopf: {
      beteiligte: gbu.beteiligte ?? null,
      unterlagen: gbu.unterlagen ?? null,
      vorschriften: textListe(gbu.vorschriften),
      psaGrundausstattung: gbu.psaGrundausstattung ?? null,
      randbedingungen: gbu.randbedingungen ?? null,
      umwelt: gbu.umwelt ?? null,
      koordination: gbu.koordination ?? null,
    },
    orgMassnahmen: textListe(gbu.orgMassnahmen),
    notfall: {
      ersteHilfe: gbu.notfall?.ersteHilfe ?? '',
      brand: gbu.notfall?.brand ?? '',
      notruf: gbu.notfall?.notruf ?? '',
      zugaenge: gbu.notfall?.zugaenge ?? '',
      meldung: gbu.notfall?.meldung ?? '',
      kontakte: gbu.notfall?.kontakte ?? '',
    },
    rollen: {
      erstellt: rolle(gbu.rollen?.erstellt),
      geprueft: gbu.rollen?.geprueft ? rolle(gbu.rollen.geprueft) : null,
      freigegeben: rolle(gbu.rollen?.freigegeben),
    },
    gefaehrdungen,
    erstelltAm: meta.erstelltAm,
    pruefenBis: meta.pruefenBis,
    kataloge: { faktoren: GEFAEHRDUNGSFAKTOREN_FASSUNG, psa: PSA_KATALOG_FASSUNG, arbmedvv: ARBMEDVV_FASSUNG },
  };
};

// ─── Massnahmenliste ────────────────────────────────────────────────────────

/**
 * Stand einer Massnahme:
 *   wirksam === true                              → 'wirksam' (geschlossen)
 *   wirksam === false, keine neuere Meldung       → 'unwirksam'
 *   umgesetzt gemeldet (und nicht abgeschlossen)  → 'gemeldet'
 *   sonst                                         → 'offen'
 * Eine Meldung NACH einer Pruefung „unwirksam" ist ein neuer Anlauf und
 * zaehlt wieder als 'gemeldet'.
 */
export const massnahmeStand = (
  m: Pick<SifaGbuMassnahme, 'wirksam' | 'wirksamGeprueftAm' | 'umgesetztGemeldetAm'>,
): MassnahmeListenZeile['stand'] => {
  if (m.wirksam === true) return 'wirksam';
  if (m.wirksam === false) {
    const gemeldet = m.umgesetztGemeldetAm || '';
    const geprueft = m.wirksamGeprueftAm || '';
    return gemeldet && geprueft && gemeldet > geprueft ? 'gemeldet' : 'unwirksam';
  }
  return m.umgesetztGemeldetAm ? 'gemeldet' : 'offen';
};

export interface MassnahmenListeOptionen {
  /**
   * Auch den ENTWURFSSTAND zeigen („auch Entwürfe"): nie freigegebene GBUs
   * kommen hinzu, und eine Fortschreibung zeigt ihre aktiven Entwurfszeilen
   * statt der gueltigen Fassung. Ohne die Option zaehlt nur, was gilt.
   */
  mitEntwuerfen?: boolean;
}

/** Die gueltige (zuletzt freigegebene) Fassung einer GBU, sofern geladen. */
const gueltigeFassung = (voll: GbuVoll): SifaGbuFassung | null => {
  const nr = voll.gbu.freigegebeneFassung;
  if (nr === null || nr === undefined) return null;
  return (voll.fassungen || []).find((f) => f.fassung === nr && Array.isArray(f.inhalt?.gefaehrdungen)) ?? null;
};

/** Ist die GBU eine Fortschreibung — schon einmal freigegeben, jetzt wieder im Entwurf bzw. in Freigabe? */
const istFortschreibung = (gbu: SifaGbu): boolean =>
  gbu.freigegebeneFassung !== null && gbu.freigegebeneFassung !== undefined
  && (gbu.status === 'entwurf' || gbu.status === 'in_freigabe');

/**
 * Massnahmen- und Terminliste ueber mehrere GBUs (Plan §4.5).
 *
 * WELCHER STAND je GBU (ohne `mitEntwuerfen`):
 *   • freigegeben            → die aktiven Live-Zeilen (sie SIND die gueltige
 *                              Fassung; entfallene fehlen).
 *   • Fortschreibung         → die Massnahmen der GUELTIGEN Fassung
 *     (Entwurf/in Freigabe     (`fassungen[freigegebeneFassung].inhalt`): Text,
 *     mit freigegebener        Art, Verantwortlich, Termin, Titel, Bereich und
 *     Vorfassung)              Gefaehrdung aus dem eingefrorenen Inhalt; der
 *                              Umsetzungs- und Wirksamkeitsstand aus der
 *                              Live-Zeile mit derselben Id (die Ids bleiben ueber
 *                              alle Fassungen gleich, Plan §4.3) — auch wenn sie
 *                              im Entwurf inzwischen entfallen ist; fehlt sie
 *                              ganz, ist der Stand 'offen'. Ist die Fassung nicht
 *                              geladen, die aktiven Live-Zeilen (lieber den
 *                              Entwurf zeigen als die Massnahmen verlieren).
 *   • nie freigegeben        → nur mit `mitEntwuerfen`.
 * Mit `mitEntwuerfen` gilt fuer jede GBU der Entwurfsstand (aktive Live-Zeilen).
 * Archivierte GBUs fallen immer heraus — ihre Massnahmen sollen niemanden
 * mehr mahnen.
 *
 * Reihenfolge: GBU-Nummer, Gefaehrdung (Nummernfolge), Massnahme; die
 * Oberflaeche sortiert fuer ihre Ansicht selbst um. `ueberfaellig` = Termin
 * vor `heute` und nicht wirksam — eine gemeldete, aber noch nicht gepruefte
 * Massnahme bleibt ueberfaellig, bis die Wirksamkeitspruefung sie schliesst.
 */
export const massnahmenListe = (
  volle: ReadonlyArray<GbuVoll>,
  kataster: Kataster,
  heute: string,
  optionen: MassnahmenListeOptionen = {},
): MassnahmeListenZeile[] => {
  const mitEntwuerfen = optionen.mitEntwuerfen === true;
  const bereiche = new Map((kataster.bereiche || []).map((b) => [b.id, b.bezeichnung]));
  const heuteTag = tag(heute);
  const zeilen: MassnahmeListenZeile[] = [];
  const zeile = (
    gbu: SifaGbu,
    kopf: { titel: string; bereich: string; gefaehrdung: string },
    m: { id: string; art: MassnahmeArt; text: string; verantwortlich: string | null; termin: string | null },
    stand: MassnahmeListenZeile['stand'],
  ) => {
    const termin = m.termin ?? null;
    zeilen.push({
      gbuId: gbu.id,
      gbuNummer: gbu.nummer,
      gbuTitel: kopf.titel,
      bereich: kopf.bereich,
      gefaehrdung: kopf.gefaehrdung,
      massnahmeId: m.id,
      art: m.art,
      text: m.text,
      verantwortlich: m.verantwortlich ?? null,
      termin,
      stand,
      ueberfaellig: !!termin && tag(termin) < heuteTag && stand !== 'wirksam',
    });
  };

  const geordnet = [...volle]
    .filter((v) => v?.gbu && v.gbu.status !== 'archiviert')
    .sort((a, b) => a.gbu.nummer - b.gbu.nummer);

  for (const voll of geordnet) {
    const { gbu } = voll;
    const nieFreigegeben = gbu.status !== 'freigegeben' && (gbu.freigegebeneFassung === null || gbu.freigegebeneFassung === undefined);
    if (nieFreigegeben && !mitEntwuerfen) continue;

    const fassung = !mitEntwuerfen && istFortschreibung(gbu) ? gueltigeFassung(voll) : null;
    if (fassung) {
      // Stand per Id aus ALLEN Live-Zeilen — auch entfallenen.
      const live = new Map((voll.massnahmen || []).map((m) => [m.id, m]));
      const inhalt = fassung.inhalt;
      const kopf = { titel: inhalt.titel ?? gbu.titel, bereich: inhalt.bereich?.bezeichnung ?? '' };
      for (const g of inhalt.gefaehrdungen) {
        for (const m of g.massnahmen || []) {
          const l = live.get(m.id);
          zeile(gbu, { ...kopf, gefaehrdung: g.gefaehrdung }, m, l ? massnahmeStand(l) : 'offen');
        }
      }
      continue;
    }

    const massnahmen = voll.massnahmen || [];
    const kopf = { titel: gbu.titel, bereich: bereiche.get(gbu.bereichId) ?? '' };
    for (const g of aktiveGefaehrdungen(voll)) {
      for (const m of massnahmenVon(massnahmen, g.id)) {
        zeile(gbu, { ...kopf, gefaehrdung: g.gefaehrdung }, m, massnahmeStand(m));
      }
    }
  }
  return zeilen;
};

// ─── Bearbeitbarkeit ────────────────────────────────────────────────────────

/**
 * Nur ein Entwurf ist bearbeitbar. Eine Fassung in Freigabe ist eingefroren
 * (zurueckziehen fuehrt zum Entwurf zurueck), eine freigegebene wird
 * fortgeschrieben (neue Fassung als Entwurf), eine archivierte gar nicht.
 */
export const istBearbeitbar = (gbu: Pick<SifaGbu, 'status'> | null | undefined): boolean =>
  gbu?.status === 'entwurf';
